import {
  Box,
  ComponentMultiStyleConfig,
  Icon,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../lib/styles/props/component-style-props";
import { IconType } from "react-icons";
import { CMDatetimeText } from "../../text";
import { ExtendedRefs, ReferenceType } from "@floating-ui/react";

export type CMPopupCommentEditRequestProps = {
  icon?: IconType;
  title?: string;
  senderName?: string;
  date?: Date;
  mainText?: string;

  /**
   * フローティング用のref
   */
  refs?: ExtendedRefs<ReferenceType>;

  /**
   * フローティング用のスタイル
   */
  floatingStyles?: React.CSSProperties;
} & ComponentStyleProps;

/**
 * 修正依頼・対応本文フローティングエリア
 */
export const CMPopupCommentEditRequest = ({
  icon,
  title,
  senderName,
  date,
  mainText,

  refs,
  floatingStyles,
  sx,
  ...rest
}: CMPopupCommentEditRequestProps) => {
  const styles = useMultiStyleConfig("CMPopupCommentEditRequestStyles");

  /**
   * 開閉状態
   * trueの場合: 開いている状態
   */
  const [isOpen, setStatus] = useState<boolean>(false);

  /**
   * 本文の開閉スタイル
   */
  const appliedCommentStyle = useMemo(
    () => (isOpen ? styles.mainTextOpen : styles.mainTextClose),
    [isOpen, styles],
  );

  /**
   * イベント
   */
  const _onToggle = () => {
    const toggledValue = !isOpen;
    setStatus(toggledValue);
  };

  return (
    <Box
      ref={refs?.setFloating}
      style={floatingStyles}
      onClick={_onToggle}
      sx={{ ...styles.container, ...sx }}
      {...rest}
    >
      <Box sx={styles.headerArea}>
        {/*アイコン*/}
        <Icon as={icon} sx={styles.icon} />

        <Box sx={styles.titleArea}>
          {/*タイトル*/}
          <Text sx={styles.title}>{title}</Text>
          <Box sx={styles.senderArea}>
            {/*送信者情報*/}
            <Text sx={styles.senderName}>{senderName}</Text>
            {/*日付*/}
            <CMDatetimeText value={date} sx={styles.date} />
          </Box>
        </Box>
      </Box>

      {/*本文*/}
      <Text sx={appliedCommentStyle}>{mainText}</Text>
    </Box>
  );
};

export const CMPopupCommentEditRequestStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "headerArea",
    "icon",
    "titleArea",
    "title",
    "senderArea",
    "senderName",
    "date",
    "mainTextOpen",
    "mainTextClose",
  ],

  baseStyle: {
    container: {
      w: "364px",
      display: "flex",
      flexDirection: "column",
      border: "solid 2px",
      borderRadius: "4px",
      borderColor: "orange.500",
      backgroundColor: "orange.100",
      p: "8px",
    },
    headerArea: {
      display: "flex",
      flexDirection: "row",
    },
    icon: {
      w: "24px",
      h: "24px",
      color: "orange.500",
    },
    titleArea: {
      display: "flex",
      flexDirection: "column",
      marginLeft: "8px",
      marginTop: "-2px",
    },
    title: {
      maxW: "310px",
      fontFamily: "Inter",
      fontStyle: "normal",
      fontWeight: "700",
      lineHeight: "24px",
      fontSize: "16px",
    },

    senderArea: {
      display: "flex",
      flexDirection: "row",
    },
    senderName: {
      maxW: "190px",
      fontFamily: "Inter",
      fontStyle: "normal",
      fontWeight: "700",
      lineHeight: "20px",
      fontSize: "14px",
    },

    date: {
      color: "gray.500",
      fontFamily: "Inter",
      fontStyle: "normal",
      fontWeight: "400",
      lineHeight: "16px",
      fontSize: "12px",
      paddingLeft: "10px",
      paddingTop: "3px",
    },
    mainTextOpen: {
      fontFamily: "Inter",
      fontStyle: "normal",
      fontWeight: "400",
      lineHeight: "20px",
      fontSize: "14px",
      color: "gray.900",
      paddingLeft: "4px",
      paddingRight: "8px",
      whiteSpace: "pre-wrap",

      h: "fit-content",
      overflow: "hidden",
    },
    mainTextClose: {
      fontFamily: "Inter",
      fontStyle: "normal",
      fontWeight: "400",
      lineHeight: "20px",
      fontSize: "14px",
      color: "gray.900",
      paddingLeft: "4px",
      paddingRight: "8px",
      whiteSpace: "pre-wrap",

      display: "-webkit-box",
      // "-webkit-box-orient": "vertical",
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      // "-webkit-line-clamp": "1",
      WebkitLineClamp: "1",
    },
  },
};
